import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Download, Users, Package, Mail, Loader2, ExternalLink, FileText, Globe, Settings as SettingsIcon, HelpCircle, Star } from "lucide-react";
import Seo from "../components/Seo";
import PackageFormModal from "../components/PackageFormModal";
import VisaFormModal from "../components/VisaFormModal";
import BlogFormModal from "../components/BlogFormModal";
import FAQFormModal from "../components/FAQFormModal";
import TestimonialFormModal from "../components/TestimonialFormModal";
import SettingsForm from "../components/SettingsForm";

function Tab({ id, active, onClick, icon: Icon, label, badge }) {
  return <button onClick={() => onClick(id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition ${active ? "bg-[#0A2540] text-white" : "bg-slate-100 text-[#0A2540] hover:bg-slate-200"}`} data-testid={`admin-tab-${id}`}><Icon className="w-4 h-4" /> {label} {badge > 0 && <span className={`ml-1 text-xs px-2 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-[#FF7D00] text-white"}`}>{badge}</span>}</button>;
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [packages, setPackages] = useState([]);
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showPkgForm, setShowPkgForm] = useState(false);
  const [editPkg, setEditPkg] = useState(null);
  const [visas, setVisas] = useState([]);
  const [showVisaForm, setShowVisaForm] = useState(false);
  const [editVisa, setEditVisa] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editBlog, setEditBlog] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [editFaq, setEditFaq] = useState(null);
  const [tests, setTests] = useState([]);
  const [showTestForm, setShowTestForm] = useState(false);
  const [editTest, setEditTest] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/leads").then(r => setLeads(r.data));
    api.get("/packages").then(r => setPackages(r.data));
    api.get("/admin/newsletter").then(r => setSubs(r.data));
    api.get("/visa").then(r => setVisas(r.data));
    api.get("/blog").then(r => setBlogs(r.data));
    api.get("/admin/faqs").then(r => setFaqs(r.data));
    api.get("/admin/testimonials").then(r => setTests(r.data));
  }, [user]);

  const reloadVisas = () => api.get("/visa").then(r => setVisas(r.data));
  const reloadBlogs = () => api.get("/blog").then(r => setBlogs(r.data));
  const reloadFaqs = () => api.get("/admin/faqs").then(r => setFaqs(r.data));
  const reloadTests = () => api.get("/admin/testimonials").then(r => setTests(r.data));

  if (loading) return <div className="pt-32 container-p muted">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const updateLeadStatus = async (id, status) => {
    setBusy(true);
    try { await api.patch(`/admin/leads/${id}`, { status }); setLeads(leads.map(l => l.id === id ? { ...l, status } : l)); toast.success("Updated"); } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const exportLeadsCSV = () => {
    const headers = ["Name", "Phone", "WhatsApp", "Email", "Destination", "Travel Date", "Travelers", "Budget", "Message", "Source", "Status", "Created"];
    const rows = leads.map(l => [l.name, l.phone, l.whatsapp, l.email, l.destination, l.travel_date, l.travelers, l.budget, (l.message || "").replace(/"/g, '""'), l.source, l.status, l.created_at]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${(v ?? "").toString()}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `leads-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const deletePkg = async (id) => {
    if (!confirm("Delete this package?")) return;
    await api.delete(`/admin/packages/${id}`); setPackages(packages.filter(p => p.id !== id)); toast.success("Deleted");
  };

  const deleteVisa = async (id) => {
    if (!confirm("Delete this visa country?")) return;
    await api.delete(`/admin/visa/${id}`); reloadVisas(); toast.success("Deleted");
  };
  const deleteBlog = async (id) => {
    if (!confirm("Delete this blog post?")) return;
    await api.delete(`/admin/blog/${id}`); reloadBlogs(); toast.success("Deleted");
  };
  const deleteFaq = async (id) => {
    if (!confirm("Delete this FAQ?")) return;
    await api.delete(`/admin/faqs/${id}`); reloadFaqs(); toast.success("Deleted");
  };
  const deleteTest = async (id) => {
    if (!confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`); reloadTests(); toast.success("Deleted");
  };

  const newLeadCount = leads.filter(l => l.status === "new").length;

  return (
    <div className="min-h-screen bg-slate-50 pt-24 lg:pt-28 pb-20">
      <Seo title="Admin Dashboard" noindex path="/admin" />
      <div className="container-p">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="h1">Admin Dashboard</h1>
            <p className="muted mt-1 text-sm">Welcome back, {user.name}</p>
          </div>
          <div className="flex gap-3">
            <Link to="/" className="btn-outline text-sm px-5 py-2.5" data-testid="admin-view-site"><ExternalLink className="w-4 h-4 mr-2" /> View Site</Link>
            <button onClick={() => logout()} className="btn-secondary text-sm px-5 py-2.5" data-testid="admin-logout"><LogOut className="w-4 h-4 mr-2" /> Logout</button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Leads", value: leads.length, icon: Users, color: "text-[#FF7D00]" },
            { label: "New Leads", value: newLeadCount, icon: Mail, color: "text-[#006CE4]" },
            { label: "Packages", value: packages.length, icon: Package, color: "text-[#0A2540]" },
            { label: "Subscribers", value: subs.length, icon: Mail, color: "text-[#FF7D00]" },
          ].map(s => (
            <div key={s.label} className="card-glass p-5">
              <s.icon className={`w-6 h-6 ${s.color} mb-2`} strokeWidth={1.5} />
              <div className="font-display font-bold text-3xl text-[#0A2540]">{s.value}</div>
              <div className="text-xs muted">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <Tab id="leads" active={tab === "leads"} onClick={setTab} icon={Users} label="Leads" badge={newLeadCount} />
          <Tab id="packages" active={tab === "packages"} onClick={setTab} icon={Package} label="Packages" />
          <Tab id="visa" active={tab === "visa"} onClick={setTab} icon={Globe} label="Visa" />
          <Tab id="blog" active={tab === "blog"} onClick={setTab} icon={FileText} label="Blog" />
          <Tab id="faqs" active={tab === "faqs"} onClick={setTab} icon={HelpCircle} label="FAQs" />
          <Tab id="tests" active={tab === "tests"} onClick={setTab} icon={Star} label="Testimonials" />
          <Tab id="subs" active={tab === "subs"} onClick={setTab} icon={Mail} label="Newsletter" />
          <Tab id="settings" active={tab === "settings"} onClick={setTab} icon={SettingsIcon} label="Settings" />
        </div>

        {showPkgForm && (
          <PackageFormModal
            initial={editPkg}
            onClose={() => { setShowPkgForm(false); setEditPkg(null); }}
            onSaved={() => api.get("/packages").then((r) => setPackages(r.data))}
          />
        )}
        {showVisaForm && (
          <VisaFormModal initial={editVisa} onClose={() => { setShowVisaForm(false); setEditVisa(null); }} onSaved={reloadVisas} />
        )}
        {showBlogForm && (
          <BlogFormModal initial={editBlog} onClose={() => { setShowBlogForm(false); setEditBlog(null); }} onSaved={reloadBlogs} />
        )}
        {showFaqForm && (
          <FAQFormModal initial={editFaq} onClose={() => { setShowFaqForm(false); setEditFaq(null); }} onSaved={reloadFaqs} />
        )}
        {showTestForm && (
          <TestimonialFormModal initial={editTest} onClose={() => { setShowTestForm(false); setEditTest(null); }} onSaved={reloadTests} />
        )}

        {tab === "leads" && (
          <div className="card-glass overflow-hidden">
            <div className="p-4 flex items-center justify-between border-b border-slate-100"><div className="font-semibold text-[#0A2540]">All Leads ({leads.length})</div><button onClick={exportLeadsCSV} className="btn-outline text-sm px-4 py-2" data-testid="admin-export-csv"><Download className="w-4 h-4 mr-2" /> Export CSV</button></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider muted"><tr><th className="text-left p-3">Name</th><th className="text-left p-3">Phone</th><th className="text-left p-3">Destination</th><th className="text-left p-3">Budget</th><th className="text-left p-3">Source</th><th className="text-left p-3">Status</th><th className="text-left p-3">Date</th></tr></thead>
                <tbody>
                  {leads.map(l => (
                    <tr key={l.id} className="border-t border-slate-100" data-testid={`lead-row-${l.id}`}>
                      <td className="p-3 font-medium text-[#0A2540]">{l.name}<div className="text-xs muted">{l.email}</div></td>
                      <td className="p-3">{l.phone}</td>
                      <td className="p-3">{l.destination || "-"}</td>
                      <td className="p-3">{l.budget || "-"}</td>
                      <td className="p-3 text-xs muted">{l.source}</td>
                      <td className="p-3"><select value={l.status} onChange={(e) => updateLeadStatus(l.id, e.target.value)} disabled={busy} className="text-xs bg-slate-100 rounded-full px-3 py-1" data-testid={`lead-status-${l.id}`}><option value="new">New</option><option value="contacted">Contacted</option><option value="converted">Converted</option><option value="lost">Lost</option></select></td>
                      <td className="p-3 text-xs muted">{new Date(l.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {leads.length === 0 && <tr><td colSpan="7" className="p-10 text-center muted">No leads yet — share your site!</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "packages" && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <div className="text-sm muted">{packages.length} active packages</div>
              <button onClick={() => { setEditPkg(null); setShowPkgForm(true); }} className="btn-primary text-sm px-5 py-2.5" data-testid="pkg-add-btn">+ Add Package</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.map(p => (
              <div key={p.id} className="card-glass p-4 flex gap-3">
                <img src={p.image} alt="" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0A2540] truncate">{p.title}</div>
                  <div className="text-xs muted">{p.destination} · {p.duration}</div>
                  <div className="font-display font-bold text-[#0A2540] mt-1">₹{p.price.toLocaleString("en-IN")}</div>
                  <div className="flex gap-3 mt-2">
                    <Link to={`/packages/${p.slug}`} className="text-xs text-[#006CE4] font-medium">View</Link>
                    <button onClick={() => { setEditPkg(p); setShowPkgForm(true); }} className="text-xs text-[#FF7D00] font-medium" data-testid={`pkg-edit-${p.id}`}>Edit</button>
                    <button onClick={() => deletePkg(p.id)} className="text-xs text-red-500 font-medium" data-testid={`pkg-delete-${p.id}`}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
        )}

        {tab === "subs" && (
          <div className="card-glass overflow-hidden">
            <div className="p-4 border-b border-slate-100 font-semibold text-[#0A2540]">Newsletter Subscribers ({subs.length})</div>
            <div className="max-h-[60vh] overflow-y-auto">
              {subs.map(s => (
                <div key={s.id} className="p-4 border-t border-slate-50 flex items-center justify-between text-sm"><span>{s.email}</span><span className="text-xs muted">{new Date(s.created_at).toLocaleDateString()}</span></div>
              ))}
              {subs.length === 0 && <div className="p-10 text-center muted">No subscribers yet.</div>}
            </div>
          </div>
        )}

        {tab === "visa" && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <div className="text-sm muted">{visas.length} visa countries</div>
              <button onClick={() => { setEditVisa(null); setShowVisaForm(true); }} className="btn-primary text-sm px-5 py-2.5" data-testid="visa-add-btn">+ Add Visa</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visas.map(v => (
                <div key={v.id} className="card-glass p-4 flex gap-3">
                  <img src={v.image} alt="" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0A2540] truncate flex items-center gap-1"><span>{v.flag}</span>{v.country}</div>
                    <div className="text-xs muted">{v.processing_time}</div>
                    <div className="font-display font-bold text-[#0A2540] mt-1">from ₹{(v.fees_from || 0).toLocaleString("en-IN")}</div>
                    <div className="flex gap-3 mt-2">
                      <Link to={`/visa/${v.slug}`} className="text-xs text-[#006CE4] font-medium">View</Link>
                      <button onClick={() => { setEditVisa(v); setShowVisaForm(true); }} className="text-xs text-[#FF7D00] font-medium" data-testid={`visa-edit-${v.id}`}>Edit</button>
                      <button onClick={() => deleteVisa(v.id)} className="text-xs text-red-500 font-medium" data-testid={`visa-delete-${v.id}`}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "blog" && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <div className="text-sm muted">{blogs.length} published posts</div>
              <button onClick={() => { setEditBlog(null); setShowBlogForm(true); }} className="btn-primary text-sm px-5 py-2.5" data-testid="blog-add-btn">+ New Post</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {blogs.map(b => (
                <div key={b.id} className="card-glass p-4 flex gap-3">
                  <img src={b.image} alt="" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#006CE4] font-semibold">{b.category}</div>
                    <div className="font-semibold text-[#0A2540] truncate">{b.title}</div>
                    <div className="text-xs muted truncate">{b.excerpt}</div>
                    <div className="flex gap-3 mt-2">
                      <Link to={`/blog/${b.slug}`} className="text-xs text-[#006CE4] font-medium">View</Link>
                      <button onClick={() => { setEditBlog(b); setShowBlogForm(true); }} className="text-xs text-[#FF7D00] font-medium" data-testid={`blog-edit-${b.id}`}>Edit</button>
                      <button onClick={() => deleteBlog(b.id)} className="text-xs text-red-500 font-medium" data-testid={`blog-delete-${b.id}`}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "faqs" && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <div className="text-sm muted">{faqs.length} FAQs on Contact page</div>
              <button onClick={() => { setEditFaq(null); setShowFaqForm(true); }} className="btn-primary text-sm px-5 py-2.5" data-testid="faq-add-btn">+ Add FAQ</button>
            </div>
            <div className="space-y-3">
              {faqs.map(f => (
                <div key={f.id} className="card-glass p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1"><div className="font-semibold text-[#0A2540]">{f.q}</div><div className="muted text-sm mt-1">{f.a}</div></div>
                    <div className="flex gap-3 flex-shrink-0">
                      <button onClick={() => { setEditFaq(f); setShowFaqForm(true); }} className="text-xs text-[#FF7D00] font-medium" data-testid={`faq-edit-${f.id}`}>Edit</button>
                      <button onClick={() => deleteFaq(f.id)} className="text-xs text-red-500 font-medium" data-testid={`faq-delete-${f.id}`}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "settings" && <SettingsForm />}

        {tab === "tests" && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <div className="text-sm muted">{tests.length} testimonials</div>
              <button onClick={() => { setEditTest(null); setShowTestForm(true); }} className="btn-primary text-sm px-5 py-2.5" data-testid="testimonial-add-btn">+ Add Testimonial</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tests.map(t => (
                <div key={t.id} className={`card-glass p-5 ${!t.active ? "opacity-50" : ""}`}>
                  <div className="flex gap-3 items-start">
                    {t.image ? <img src={t.image} alt="" className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center font-display font-semibold text-[#0A2540]">{t.name?.[0]}</div>}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#0A2540] truncate">{t.name}</div>
                      <div className="text-xs muted truncate">{t.trip} · {t.location}</div>
                      <div className="flex gap-0.5 mt-1">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} className="w-3 h-3 fill-[#FF7D00] text-[#FF7D00]" />)}</div>
                    </div>
                  </div>
                  <p className="text-sm muted mt-3 line-clamp-3">"{t.text}"</p>
                  <div className="flex gap-3 mt-3">
                    <button onClick={() => { setEditTest(t); setShowTestForm(true); }} className="text-xs text-[#FF7D00] font-medium" data-testid={`testimonial-edit-${t.id}`}>Edit</button>
                    <button onClick={() => deleteTest(t.id)} className="text-xs text-red-500 font-medium" data-testid={`testimonial-delete-${t.id}`}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
